const normalizeAnchorMessageId = (value) => (value == null ? '' : String(value).trim());

const resolveContainerElement = (containerOrRef) => {
  if (!containerOrRef) return null;
  if (typeof containerOrRef === 'object' && 'current' in containerOrRef) {
    return containerOrRef.current || null;
  }
  return containerOrRef;
};

const escapeAttrValue = (value) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return String(value).replace(/["\\]/g, '\\$&');
};

const raf =
  typeof requestAnimationFrame === 'function'
    ? (cb) => requestAnimationFrame(cb)
    : (cb) => setTimeout(() => cb(), 16);

// 直近のジャンプ（検索結果 / リプライ）だけを有効にするためのトークン。
// 新しいジャンプが来たら、まだ対象行の出現を待っている古いポーリングを打ち切る。
let anchorScrollToken = 0;

export const clearChatAnchorHighlight = (timerRef) => {
  if (!timerRef?.current) return;
  clearTimeout(timerRef.current);
  timerRef.current = null;
};

/**
 * 検索結果 / リプライから対象メッセージへ確実にスクロールしてハイライトする。
 *
 * 以前は setMessages 直後に1回だけ requestAnimationFrame → querySelector していたため、
 * 対象行がまだ DOM にコミットされておらず（あるいは画像などで後からレイアウトがずれて）
 * 「表示されるときとされないときがある」状態だった。
 *
 * 対策:
 *  - 対象行がマウントされるまで rAF で数フレーム再試行してからスクロールする。
 *  - 画像などの遅延レイアウトで位置がずれるため、少し後に一度だけ位置を再補正する。
 *  - 新しいジャンプが来たら古い待機を打ち切る（トークン）。
 */
export const focusChatAnchorMessage = ({
  messageId,
  containerRef,
  setActiveAnchorMessageId,
  timerRef,
  durationMs = 3000,
  behavior = 'smooth',
  block = 'center',
  maxAttempts = 60, // 対象行がマウントされるまで最大 ~60フレーム（約1秒）待つ
} = {}) => {
  const normalizedMessageId = normalizeAnchorMessageId(messageId);
  if (!normalizedMessageId || typeof setActiveAnchorMessageId !== 'function') return;

  clearChatAnchorHighlight(timerRef);
  setActiveAnchorMessageId(normalizedMessageId);

  const myToken = (anchorScrollToken += 1);
  const selector = `[data-mid="${escapeAttrValue(normalizedMessageId)}"]`;

  const findTarget = () => {
    const container = resolveContainerElement(containerRef);
    return container ? container.querySelector(selector) : null;
  };

  const scrollToTarget = (behaviorOverride) => {
    const target = findTarget();
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block, behavior: behaviorOverride || behavior });
      return true;
    }
    return false;
  };

  let attempts = 0;
  const tick = () => {
    if (myToken !== anchorScrollToken) return; // 新しいジャンプに置き換えられた
    if (scrollToTarget()) {
      // 画像や引用など遅延レイアウトで位置がずれることがあるため、一度だけ再補正する。
      setTimeout(() => {
        if (myToken !== anchorScrollToken) return;
        scrollToTarget('auto');
      }, 160);
      return;
    }
    attempts += 1;
    if (attempts <= maxAttempts) raf(tick);
  };
  raf(tick);

  if (!timerRef) return;
  timerRef.current = setTimeout(() => {
    setActiveAnchorMessageId((prev) => (prev === normalizedMessageId ? '' : prev));
    timerRef.current = null;
  }, durationMs);
};
